import * as React from "react";
import {
  Form,
  Checkbox,
  Input,
  Row,
  Col,
  Card,
  Space,
  Button,
  Tooltip,
  Select
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import Constants, { CaseDecisionStatus } from "../../common/Constants";
import AttachmentTable from "../../../caseLawDocuments/ui/components/AttachmentTable";
import { ICourtCase } from "../../../caseLawDocuments/model/CourtCase";
import { TCaseDecisionPresentationModel } from "../../presenter/model/CaseDecisionPresentationModel";
import { IOrganisation } from "../../../organisations/model/Organisation";
import { TAttachmentTypePresentationModel } from "../../../organisations/presenter/model/AttachmentTypePresentationModel";
import { TAttachmentPresentationModel } from "../../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import { IGenericCodebookItem } from "../../../codebooks/model/GenericCodebookItem";
import { IAttachment } from "../../../caseLawDocuments/model/Attachment";
import { IDocumentType } from "../../../documentTypes/model/DocumentType";
import FormSection from "../../../core/ui/components/FormSection";
import DateTimePicker from "../../../core/ui/components/DateTimePicker";
import { DateFormats } from "../../../core/common/Constants";
import moment from "moment";
import { ContentTypes } from "../../../caseLawDocuments/common/Constants";
import AttachmentForm from "../../../caseLawDocuments/ui/components/AttachmentForm";
import UpdateModal from "../../../core/ui/components/grid/UpdateModal";
import SelectInput from "../../../core/ui/components/SelectInput";
import { IDocumentTypeIndexCategory } from "../../../documentTypes/model/DocumentTypeIndexCategory";
import { AttachmentType } from "../../../attachmentType/common/Constants";
import { TOrganisationIndexGroupPresentationModel } from "../../../organisations/presenter/model/OrganisationIndexGroupPresentationModel";
import { IIndexTypeAttribute } from "../../../indexTypes/model/IndexTypeAttribute";
import { IIndexAttributeData } from "../../../indexTypes/model/IndexAttributeData";
import IndexAttributeDataForm from "../../../caseLawDocuments/ui/components/IndexAttributeDataForm";

const { Option } = Select;
const FormFields = {
  ID: "id",
  NAME: "name",
  STATUS: "status",
  CASE_DECISION_TYPE_ID: "documentTypeId",
  ORGANISATION_ID: "organisationId",
  PARENT_GROUP: "parentGroup",
  SECOND_GROUP: "secondGroup",
  ORGANISATION_INDEX_LIST: "organisationIndexList",
  DATE: "decisionDate",
  VALID: "valid"
};

const CaseDecisionUpdateForm = ({
  translate,
  caseDecision,
  caseDecisionTypes,
  attachmentTypes,
  contentTypes,
  attachmentList,
  uploadedFile,
  createAttachment,
  setFileValue,
  updateAttachment,
  updateMainAttachment,
  setAttachmentType,
  deleteAttachment,
  showCaseDecisionDetailsPage,
  selectedParentGroupIdList,
  selectedSecondGroupIdList,
  updateCaseDecision,
  toggleAttachementModal,
  attachmentModalVisible,
  mainAttachment,
  canEdit,
  anonimisedUploadedFile,
  setAnonymisedFileValue,
  organisationIndexes,
  parentGroupList,
  secondGroupList,
  setParentGroupId,
  setSecondGroupId,
  selectedIndexes,
  setMainAttachmentType,
  indexTypeAttributes,
  setAttributesData,
  indexAttributeDataList,
  closeIndexDataModal,
  openIndexDataModal,
  indexDataModalVisible,
  saveAttributeData,
  selectedIndexForAttribute
}: {
  translate: any;
  caseDecision: TCaseDecisionPresentationModel;
  organisations: IOrganisation[];
  caseDecisionTypes: IDocumentType[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  contentTypes: IGenericCodebookItem[];
  attachmentList: TAttachmentPresentationModel[];
  uploadedFile: any;
  indexHierarchyList: string[][];
  createAttachment: (attachment: IAttachment) => void;
  setFileValue: (file) => void;
  updateAttachment: (attachment: any) => void;
  updateMainAttachment: (attachment: any) => void;
  setAttachmentType: (attachmentType: string) => void;
  deleteAttachment: (attachment: TAttachmentPresentationModel) => void;
  showCaseDecisionDetailsPage: (id: string) => void;
  updateCaseDecision: (item: any) => void;
  mainAttachment: TAttachmentPresentationModel;
  courtCase: ICourtCase;
  selectedParentGroupIdList: number[];
  selectedSecondGroupIdList: number[];
  organisationIndexes: IDocumentTypeIndexCategory[];
  toggleAttachementModal: any;
  attachmentModalVisible: boolean;
  parentGroupList: TOrganisationIndexGroupPresentationModel[][];
  secondGroupList: TOrganisationIndexGroupPresentationModel[][];
  setParentGroupId: (selectedParentGroupId: number, index: number) => void;
  setSecondGroupId: (selectedSecondGroupId: number, index: number) => void;
  selectedIndexes?: any;
  publishCaseDecision: any;
  canEdit: any;
  anonimisedUploadedFile: any;
  setAnonymisedFileValue: any;
  setMainAttachmentType: any;
  indexTypeAttributes?: IIndexTypeAttribute[];
  setAttributesData?: any;
  indexAttributeDataList?: IIndexAttributeData[];
  closeIndexDataModal: any;
  openIndexDataModal: any;
  indexDataModalVisible: boolean;
  saveAttributeData: any;
  selectedIndexForAttribute: any;
}) => {
  const [form] = Form.useForm();
  const [UpdateForm] = Form.useForm();

  caseDecision &&
    form.setFieldsValue({
      documentTypeId: caseDecision.documentTypeId,
      organisationId: caseDecision.organisationId,
      name: caseDecision.name,
      id: caseDecision.id,
      valid: caseDecision.valid,
      status: caseDecision.status
        ? caseDecision.status
        : CaseDecisionStatus.IN_PROGRESS
    });

  function renderAttachmentContent() {
    return mainAttachment?.contentType?.code ===
      ContentTypes.URL_CONTENT_TYPE ? (
      <Tooltip title={mainAttachment?.content}>
        <a
          href={mainAttachment.content}
          rel="noopener noreferrer"
          target={"_blank"}
        >
          {translate("editors.case_decisions.redirect")}
        </a>
      </Tooltip>
    ) : mainAttachment?.identificationCode !== "Y" &&
      mainAttachment?.attachmentType?.anonymisationRequired ? (
      <a
        href={`/api${Constants.endpointBasePath}/${caseDecision.id}/attachments/${mainAttachment?.id}/download`}
      >
        {translate("editors.case_decisions.download")}
      </a>
    ) : (
      <div></div>
    );
  }

  function onChangeParentGroup(value, index) {
    const values = form.getFieldsValue();
    values.secondGroup[index] = undefined;
    values.organisationIndexList[index] = undefined;
    form.setFieldsValue({
      secondGroup: values.secondGroup,
      organisationIndexList: values.organisationIndexList
    });

    setParentGroupId(value, index);
    setSecondGroupId(NaN, index);
  }

  function onChangeSecondGroup(value, index) {
    const selectedParentGroupId = selectedParentGroupIdList[index];
    setSecondGroupId(value, index);
    const values = form.getFieldsValue();
    if (isNaN(selectedParentGroupId)) {
      setParentGroupId(
        secondGroupList[index].find((elem) => elem.id === value)?.parentId ??
          NaN,
        index
      );
    }
    values.parentGroup[index] =
      secondGroupList[index].find((elem) => elem.id === value)?.parentId ?? NaN;
    values.organisationIndexList[index] = undefined;
    form.setFieldsValue({
      parentGroup: values.parentGroup,
      organisationIndexList: values.organisationIndexList
    });
  }

  function filterSecondGroupValues(index) {
    const selectedParentGroupId = selectedParentGroupIdList[index];
    let newList = selectedParentGroupId
      ? secondGroupList[index].filter(
          (elem) => elem.parentId === selectedParentGroupId
        )
      : secondGroupList[index];

    return newList;
  }
  function findParent(group, groupList, data) {
    if (group?.parentId) {
      let g = groupList.find((g) => g.id === group?.parentId);
      if (g) {
        data = data + ` 🡐 ${g?.name}`;
        return findParent(g, groupList, data);
      }
    } else if (group?.organisationIndexCategory) {
      data = data + ` 🡐 ${group?.organisationIndexCategory?.name}`;
    }
    return data;
  }

  function renderOptions(list) {
    return list.map((index) => {
      let data = index.name;
      if (index.groups) {
        let firstGroup = index.groups.find(
          (g) => g.id === index.organisationGroupId
        );
        data = `${index?.name} 🡐 ${firstGroup?.name}`;
        data = findParent(firstGroup, index.groups, data);
      }
      return (
        <Option key={index.id} value={index.id} title={data} label={data}>
          {data}
        </Option>
      );
    });
  }

  function filterIndexData(list, index) {
    const selectedParentGroupId = selectedParentGroupIdList[index];
    let newList = list;
    if (selectedParentGroupId) {
      newList = list.filter(
        (item) =>
          item.groups.findIndex(
            (element) => element.id === selectedParentGroupId
          ) !== -1
      );
    }

    return newList;
  }
  return (
    <Form layout={"vertical"} form={form}>
      <Row align="middle" justify="center">
        <Col xl={20} lg={24} md={24} sm={24} xs={24}>
          <Card bordered={false}>
            <Form.Item
              name={FormFields.ID}
              initialValue={caseDecision?.id}
              style={{ display: "none" }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={translate("editors.case_decisions.valid")}
              name={FormFields.VALID}
              valuePropName={"checked"}
              initialValue={caseDecision.valid ?? true}
              style={{ display: "none" }}
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              style={{ display: "none" }}
              label={translate("editors.case_decisions.status")}
              name={FormFields.STATUS}
              initialValue={
                caseDecision?.status ?? CaseDecisionStatus.IN_PROGRESS
              }
              className={"mb-0"}
              rules={[
                {
                  required: true,
                  message: translate(
                    "editors.case_decisions.required_messages",
                    {
                      field: translate("editors.case_decisions.status")
                    }
                  )
                }
              ]}
            >
              <Input />
            </Form.Item>
            <Space direction="vertical" style={{ width: "100%" }}>
              <FormSection
                title={translate("editors.case_decisions.basic_data")}
                subtitle={translate("editors.case_decisions.basic_data")}
              >
                <Row gutter={16} style={{ marginBottom: 10, marginTop: 10 }}>
                  <Col xs={24} sm={24}>
                    <div style={{ marginTop: "3em" }}>
                      <Form.Item
                        label={translate("editors.case_decisions.name")}
                        className={"mb-0"}
                        rules={[
                          {
                            required: false
                          }
                        ]}
                      >
                        <p style={{ marginTop: "0.5em" }}>
                          {caseDecision.name}
                        </p>
                      </Form.Item>
                      {caseDecision.organisationName && (
                        <Form.Item
                          label={translate(
                            "editors.case_decisions.organisation_name"
                          )}
                          className={"mb-0"}
                          rules={[
                            {
                              required: false
                            }
                          ]}
                        >
                          <p style={{ marginTop: "attachmentType0.5em" }}>
                            {caseDecision.organisationName}
                          </p>
                        </Form.Item>
                      )}
                      {caseDecision.caseDecisionType && (
                        <Form.Item
                          label={translate(
                            "editors.case_decisions.case_decision_type"
                          )}
                          name={FormFields.CASE_DECISION_TYPE_ID}
                          className={"mb-0"}
                          initialValue={caseDecision.caseDecisionType.id}
                          rules={[
                            {
                              required: true,
                              message: translate(
                                "editors.case_decisions.required_messages",
                                {
                                  field: translate(
                                    "editors.case_decisions.case_decision_type"
                                  )
                                }
                              )
                            }
                          ]}
                        >
                          <SelectInput
                            placeholder={translate(
                              "editors.case_decisions.select_case_decision_type"
                            )}
                            allowClear
                            showSearch={true}
                            notFoundContent={translate("search.no_data")}
                            selectOptionKey={"id"}
                            selectOptionValue={"name"}
                            selectOptions={caseDecisionTypes}
                          />
                        </Form.Item>
                      )}

                      <Form.Item
                        label={translate("editors.case_decisions.date")}
                        name={FormFields.DATE}
                        initialValue={moment(
                          caseDecision.decisionDate || new Date()
                        )}
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "editors.case_law_documents.required_messages",
                              {
                                field: translate(
                                  "editors.case_law_documents.date"
                                )
                              }
                            )
                          }
                        ]}
                      >
                        <DateTimePicker
                          placeholder={translate("date_picker.placeholder")}
                          format={DateFormats.DATE}
                          showToday={true}
                          value={caseDecision.decisionDate}
                          className={"input-date-wrapper"}
                          translate={translate}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </FormSection>
              {organisationIndexes?.length > 0 && (
                <FormSection
                  title={translate("editors.case_law_documents.classification")}
                  subtitle={translate(
                    "editors.case_law_documents.sections.index_subtitle"
                  )}
                >
                  {organisationIndexes.map((item, index) => {
                    return (
                      <>
                        <Row
                          style={
                            organisationIndexes.length === 0
                              ? { display: "none" }
                              : {
                                  marginTop: 10,
                                  alignItems: "center"
                                }
                          }
                        >
                          <Col
                            md={24}
                            sm={24}
                            xs={24}
                            style={{
                              marginTop: 20,
                              alignItems: "center"
                            }}
                          >
                            <Form.Item
                              label={translate(
                                "editors.case_law_documents.parent_group"
                              )}
                              className={"mb-0"}
                              style={{
                                width: "100%",
                                marginRight: 10
                              }}
                              name={[FormFields.PARENT_GROUP, index]}
                              initialValue={selectedParentGroupIdList[index]}
                              dependencies={[`secondGroup[${index}]`]}
                            >
                              <SelectInput
                                placeholder={translate(
                                  "editors.case_law_documents.select_parent_group"
                                )}
                                allowClear
                                showSearch={true}
                                notFoundContent={translate("search.no_data")}
                                selectOptionKey={"id"}
                                selectOptionValue={"name"}
                                selectOptions={parentGroupList[index]}
                                onChange={(value) => {
                                  onChangeParentGroup(value, index);
                                }}
                              />
                            </Form.Item>
                          </Col>

                          <Col
                            span={7}
                            style={{
                              display: "none",
                              marginTop: 20,
                              alignItems: "center"
                            }}
                          >
                            <Form.Item
                              label={translate(
                                "editors.case_law_documents.second_group"
                              )}
                              className={"mb-0"}
                              style={{
                                width: "100%",
                                marginRight: 10
                              }}
                              initialValue={selectedSecondGroupIdList[index]}
                              name={[FormFields.SECOND_GROUP, index]}
                              shouldUpdate
                            >
                              <SelectInput
                                allowClear
                                placeholder={translate(
                                  "editors.case_law_documents.select_second_group"
                                )}
                                showSearch={true}
                                notFoundContent={translate("search.no_data")}
                                selectOptionKey={"id"}
                                selectOptionValue={"name"}
                                selectOptions={filterSecondGroupValues(index)}
                                onChange={(value) =>
                                  onChangeSecondGroup(value, index)
                                }
                              />
                            </Form.Item>
                          </Col>

                          <Col
                            lg={24}
                            md={24}
                            sm={24}
                            xs={24}
                            style={{
                              marginTop: 20,
                              alignItems: "center"
                            }}
                          >
                            <Form.Item
                              name={[FormFields.ORGANISATION_INDEX_LIST, index]}
                              className={"mb-0"}
                              shouldUpdate
                              label={translate(
                                "editors.case_decisions.index_data"
                              )}
                              rules={[
                                {
                                  required: item.required,
                                  message: translate(
                                    "editors.case_decisions.required_messages",
                                    {
                                      field: translate(
                                        "editors.case_decisions.case_decision_type"
                                      )
                                    }
                                  )
                                },
                                {
                                  validator: (rule, value, callback) => {
                                    if (value && item.maxLength) {
                                      if (value.length > item.maxLength) {
                                        callback(
                                          `${translate(
                                            "editors.case_law_documents.index.max_length"
                                          )} ${item.maxLength}`
                                        );
                                      } else if (
                                        value.length <= item.maxLength
                                      ) {
                                        callback();
                                      }
                                    } else {
                                      callback();
                                    }

                                    return;
                                  }
                                }
                              ]}
                              initialValue={
                                selectedIndexes.length > 0
                                  ? selectedIndexes?.map((item) => item.id)
                                  : null
                              }
                            >
                              <Select
                                placeholder={translate(
                                  "editors.case_law_documents.select_index_data"
                                )}
                                mode="multiple"
                                showSearch
                                optionFilterProp="children"
                                className={"png-tagbox"}
                                onChange={(idList: []) => {
                                  openIndexDataModal(
                                    item.organisationIndexList?.filter(
                                      (i) => i.id === idList[idList.length - 1]
                                    )[0],
                                    idList
                                  );
                                }}
                              >
                                {renderOptions(
                                  filterIndexData(
                                    item.organisationIndexList,
                                    index
                                  )
                                )}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    );
                  })}
                </FormSection>
              )}
              <FormSection
                title={translate("editors.case_decisions.main_attachment")}
                subtitle={translate("editors.case_decisions.main_attachment")}
              >
                <p>{mainAttachment?.name}</p>
                <p>{mainAttachment?.description}</p>
                <div style={{ display: "flex" }}>
                  <p>{renderAttachmentContent()}</p>
                  <a
                    style={{ marginLeft: "3em" }}
                    onClick={() => toggleAttachementModal(true)}
                  >
                    {translate("editors.case_decisions.edit")}
                  </a>
                </div>
              </FormSection>
            </Space>
          </Card>
        </Col>
      </Row>
      <Row justify="center">
        <Col
          lg={20}
          md={24}
          sm={24}
          xs={24}
          style={{ textAlign: "right", paddingTop: 20 }}
        >
          <Button
            value=""
            type="link"
            danger
            onClick={() => {
              form.resetFields();
              showCaseDecisionDetailsPage(caseDecision.id);
            }}
          >
            <CloseOutlined /> {translate("data_grid.modal.actions.cancel")}
          </Button>
          <Button
            value=""
            type={"primary"}
            onClick={() => {
              form
                .validateFields()
                .then((values) => {
                  updateCaseDecision(values);
                })
                .catch((info) => {});
            }}
          >
            <CheckOutlined /> {translate("data_grid.modal.actions.save")}
          </Button>
        </Col>
      </Row>
      <Row align="middle" justify="center" style={{ marginTop: 80 }}>
        <Col xl={20} lg={24} md={24} sm={24} xs={24}>
          <Card>
            <FormSection
              title={translate("editors.case_decisions.additional_attachments")}
              subtitle={translate(
                "editors.case_decisions.additional_attachments"
              )}
            >
              <AttachmentTable
                translate={translate}
                attachmentTypes={attachmentTypes}
                attachments={attachmentList}
                createAttachment={createAttachment}
                setFileValue={setFileValue}
                uploadedFile={uploadedFile}
                editable={true}
                updateAttachment={updateAttachment}
                setAttachmentType={setAttachmentType}
                deleteAttachment={deleteAttachment}
                contentTypes={contentTypes}
                canDownload={canEdit}
                item={caseDecision}
                organisationId={caseDecision.organisationId}
                basePath={Constants.endpointBasePath}
                anonimisedUploadedFile={anonimisedUploadedFile}
                setAnonymisedFileValue={setAnonymisedFileValue}
              />
            </FormSection>
          </Card>
        </Col>
      </Row>
      <IndexAttributeDataForm
        closeIndexDataModal={closeIndexDataModal}
        indexAttributeDataList={indexAttributeDataList}
        indexDataModalVisible={indexDataModalVisible}
        indexTypeAttributes={indexTypeAttributes}
        saveAttributeData={saveAttributeData}
        form={form}
        setAttributesData={setAttributesData}
        translate={translate}
        selectedIndexForAttribute={selectedIndexForAttribute}
      />
      <UpdateModal
        visible={attachmentModalVisible}
        translate={translate}
        title={translate("editors.organisations.update_modal")}
        className={"modal-width"}
        formInstance={UpdateForm}
        rowData={mainAttachment}
        onUpdate={updateMainAttachment}
        onCancel={() => {
          toggleAttachementModal(false);
          UpdateForm.resetFields();
        }}
        Form={
          <AttachmentForm
            translate={translate}
            form={UpdateForm}
            attachmentTypes={attachmentTypes || []}
            uploadedFile={uploadedFile}
            setFileValue={setFileValue}
            setAttachmentType={setAttachmentType}
            contentTypes={contentTypes}
            setMainAttachmentType={setMainAttachmentType}
            anonimisedUploadedFile={anonimisedUploadedFile}
            setAnonymisedFileValue={setAnonymisedFileValue}
            isMainAttachment={true}
            basePath={`/api${Constants.endpointBasePath}/${caseDecision.id}`}
          />
        }
      />
    </Form>
  );
};
export default CaseDecisionUpdateForm;
